import { services as servicesQuery } from '@data/queries';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Card, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import sanity from '@utils/sanity';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { ServiceProps } from 'src/@types/service';
import { FormContactDetails, FormService, FormSummary } from 'src/sections/form';
import * as yup from 'yup';
import { Page } from '../src/components';
import Layout from '../src/layouts';

const encode = (data: any) =>
  Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&');

// form validation
// ----------------------------------------------------------------------

const schema = yup
  .object()
  .shape({
    name: yup.string().required(),
    email: yup.string().email().required(),
    phone: yup.string().required(),
    service: yup.string().required(),
  })
  .required();

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  minHeight: '100%',
  paddingTop: theme.spacing(15),
  paddingBottom: theme.spacing(10),
}));

// ----------------------------------------------------------------------

type Props = {
  services: ServiceProps[];
};

export default function FormPage({ services }: Props) {
  const form = useForm({ resolver: yupResolver(schema) });
  const { handleSubmit } = form;

  const router = useRouter();

  const onSubmit = (data: { [x: string]: any; }) => {
    fetch('https://formspree.io/f/xvolzylb', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: encode({ 'form-name': 'contact', ...data }),
    })
      .then(() => {
        // Push form event to google analytics datalayer
        //@ts-ignore
        window.dataLayer = window.dataLayer || [];
        //@ts-ignore
        window.dataLayer.push({ event: 'form_submit', service: data.service });
      })
      .then(() => router.push('/thank-you'))
      .catch((error) => alert(error));
  };

  return (
    <RootStyle seo={{ metaTitle: 'Bestill befaring' }}>
      <Container sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
        <Typography variant="h3" paragraph >
          Etterspør elektriker / befaring
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 5 }}>
          Vi har ledig kapasitet. Fyll ut skjemaet og vi svarer deg omgående.
        </Typography>
        <Card
          sx={{
            maxWidth: "sm",
            width: 1,
            boxShadow: (theme) => ({
              xs: 0,
              md: theme.customShadows.z16,
            }),
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: 'grid',
                gap: 4,
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  md: 'repeat(1, 1fr)',
                },
              }}
            >
              <FormService form={form} services={services} />
              <FormContactDetails form={form} />
              <FormSummary form={form} />
            </Box>
          </form>
        </Card>

      </Container>
      {/* Hidden form replica that netlify can find  */}
      <form name="contact" method="post" data-netlify="true" data-netlify-honeypot="bot-field">
        <input type="hidden" name="form-name" value="contact" />
        <input type="hidden" name="service" />
        <input type="hidden" name="description" />
        <input type="hidden" name="name" />
        <input type="hidden" name="phone" />
        <input type="hidden" name="email" />
        <input type="hidden" name="inspection" />
        <input type="hidden" name="address" />
        <input type="hidden" name="consent" />
      </form>
    </RootStyle >
  );
}

// ----------------------------------------------------------------------

FormPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout simpleHeader disabledFooter>
      {page}
    </Layout>
  );
};

// -----------------------------------------------------------------------

export const getStaticProps = async () => {
  const services = await sanity.fetch(servicesQuery);

  return {
    props: { services },
  };
};
